import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

const CANADIAN_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Nova Scotia',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
];

// Format phone number as user types: (XXX) XXX-XXXX
const formatPhoneNumber = (input: string): string => {
  // Remove all non-digit characters
  const digitsOnly = input.replace(/\D/g, '');

  // Handle each segment of the phone number
  if (digitsOnly.length === 0) {
    return '';
  } else if (digitsOnly.length <= 3) {
    return `(${digitsOnly}`;
  } else if (digitsOnly.length <= 6) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  } else {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  }
};

// Validate phone number format (accepts various formats)
const isValidPhoneNumber = (phone: string): boolean => {
  // Remove all non-digit characters for validation
  const digitsOnly = phone.replace(/\D/g, '');
  // North American format: must have 10 digits
  return digitsOnly.length === 10;
};

// Validate phone extension (optional, digits only)
const isValidPhoneExtension = (ext: string): boolean => {
  if (!ext) return true; // Optional field
  // Allow only digits, max 10 characters
  return /^\d*$/.test(ext) && ext.length <= 10;
};

// Validate postal code (Canadian: A1A 1A1) or ZIP (US: 12345 or 12345-6789)
const isValidPostalCode = (code: string): boolean => {
  const normalized = code.trim().toUpperCase();

  // Canadian postal code: A1A 1A1 (letter-digit-letter space digit-letter-digit)
  const canadianPostal = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/;

  // US ZIP code: 12345 or 12345-6789
  const usZip = /^\d{5}(-\d{4})?$/;

  return canadianPostal.test(normalized) || usZip.test(normalized);
};

interface ContactInfoStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  existingData?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  onNext,
  onBack,
  existingData = {},
  isFirstStep = false,
  isLastStep = false,
}) => {
  const [phoneNumber, setPhoneNumber] = useState(existingData?.phoneNumber || '');
  const [phoneExtension, setPhoneExtension] = useState(existingData?.phoneExtension || '');
  const [dateOfBirth, setDateOfBirth] = useState(existingData?.dateOfBirth || '');
  const [addressLine1, setAddressLine1] = useState(existingData?.addressLine1 || '');
  const [addressLine2, setAddressLine2] = useState(existingData?.addressLine2 || '');
  const [city, setCity] = useState(existingData?.city || '');
  const [province, setProvince] = useState(existingData?.province || '');
  const [postalCode, setPostalCode] = useState(existingData?.postalCode || '');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    setError('');

    // Validation
    if (!phoneNumber.trim()) {
      setError('Primary phone number is required');
      return;
    }

    if (!isValidPhoneNumber(phoneNumber)) {
      setError('Please enter a valid phone number (10 digits)');
      return;
    }

    if (phoneExtension && !isValidPhoneExtension(phoneExtension)) {
      setError('Phone extension must be digits only and 10 characters or less');
      return;
    }

    if (!dateOfBirth.trim()) {
      setError('Birthday is required');
      return;
    }

    if (!addressLine1.trim()) {
      setError('Address line 1 is required');
      return;
    }

    if (!city.trim()) {
      setError('City is required');
      return;
    }

    if (!province) {
      setError('Province is required');
      return;
    }

    if (!postalCode.trim()) {
      setError('Postal code is required');
      return;
    }

    if (!isValidPostalCode(postalCode)) {
      setError('Please enter a valid postal code (A1A 1A1) or ZIP code (12345 or 12345-6789)');
      return;
    }

    const data = {
      phoneNumber: phoneNumber.trim(),
      phoneExtension: phoneExtension.trim() || undefined,
      dateOfBirth: dateOfBirth.trim(),
      addressLine1: addressLine1.trim(),
      addressLine2: addressLine2.trim() || undefined,
      city: city.trim(),
      province,
      postalCode: postalCode.trim(),
    };

    onNext(data);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Let's collect your contact information so we can reach you if needed.
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Primary Phone */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Primary phone</Typography>
          <TextField
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
            onKeyDown={(e) => {
              // Allow backspace, delete, arrow keys, etc
              const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
              if (allowedKeys.includes(e.key)) return;
              // Only allow numeric input
              if (!/^\d$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            required
            fullWidth
            placeholder="(XXX) XXX-XXXX"
            inputProps={{
              maxLength: 14, // (XXX) XXX-XXXX
            }}
          />
        </Box>

        {/* Phone Extension */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
            (Optional) Primary phone extension
          </Typography>
          <TextField
            type="tel"
            value={phoneExtension}
            onChange={(e) => {
              // Only keep digits
              const digitsOnly = e.target.value.replace(/\D/g, '');
              setPhoneExtension(digitsOnly);
            }}
            onKeyDown={(e) => {
              // Allow backspace, delete, arrow keys, etc
              const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
              if (allowedKeys.includes(e.key)) return;
              // Only allow numeric input
              if (!/^\d$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            fullWidth
            placeholder="XXXX"
            inputProps={{
              maxLength: 10,
            }}
          />
        </Box>

        {/* Birthday */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Birthday</Typography>
          <TextField
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ placeholder: 'yyyy-mm-dd' }}
          />
        </Box>

        {/* Address Line 1 */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Address line 1</Typography>
          <TextField
            type="text"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            required
            fullWidth
          />
        </Box>

        {/* Address Line 2 */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
            Address line 2 (Optional)
          </Typography>
          <TextField
            type="text"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            fullWidth
          />
        </Box>

        {/* City */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>City</Typography>
          <TextField
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
            fullWidth
          />
        </Box>

        {/* Province */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Province</Typography>
          <FormControl fullWidth required>
            <Select
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">Select Province ...</MenuItem>
              {CANADIAN_PROVINCES.map((prov) => (
                <MenuItem key={prov} value={prov}>
                  {prov}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Postal Code */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Postal Code or ZIP</Typography>
          <TextField
            type="text"
            value={postalCode}
            onChange={(e) => {
              const input = e.target.value.toUpperCase();
              // Format Canadian postal codes: A1A 1A1
              // Also allow US ZIP format: 12345 or 12345-6789
              setPostalCode(input);
            }}
            required
            fullWidth
            placeholder="A1A 1A1 or 12345"
            inputProps={{
              maxLength: 10, // A1A 1A1 or 12345-6789
            }}
          />
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isFirstStep}
          sx={{ minWidth: 120 }}
        >
          Previous Step
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ minWidth: 120 }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};
